import useUser from "@/hooks/useUser"
import { apiClientV2 } from "@/services/api/apiClient"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import type { AxiosError } from "axios"
import type {
  HelpAdminTree,
  HelpArticleCreate,
  HelpArticleEdit,
  HelpArticleHistory,
  HelpArticleSource,
  HelpCategoryCreate,
  HelpContentStatus,
  HelpGroupCreate,
  HelpOrderKind,
  HelpValidation,
} from "./types"

/**
 * The editing half of the helpdesk API, behind HELPDESK_EDIT.
 *
 * Keyed by organisation for the same reason the read path is: the QueryClient is
 * module-scoped and outlives sign-out, so nothing staff-only is left addressable
 * in a cache the next session shares.
 */

const sourceKey = (
  organization: string | undefined,
  id: string,
  lang: string,
) => ["helpdesk", "source", organization, id, lang] as const

/** The article as the editor should open it: the draft if one is waiting, else the published text. */
export function useHelpArticleSource(
  articleId: string,
  lang: string,
  enabled: boolean,
) {
  const { user } = useUser()
  const organization = user?.organization

  return useQuery({
    queryKey: sourceKey(organization, articleId, lang),
    queryFn: async (): Promise<HelpArticleSource> => {
      const response = await apiClientV2.get(
        `/help/admin/articles/${articleId}`,
        {
          params: { lang },
        },
      )
      return response.data as HelpArticleSource
    },
    enabled: enabled && Boolean(organization),
    // Always reopen on the current draft: someone else may have saved one, and a
    // stale body here is what a lost rewrite looks like.
    staleTime: 0,
  })
}

/**
 * The parse and copy check behind the live preview.
 *
 * A query keyed by the body rather than a mutation: react-query then guarantees
 * what is rendered belongs to the text currently in the box, so a slow response
 * for an older keystroke cannot overwrite a newer preview.
 */
export function useHelpArticlePreview(
  articleId: string,
  lang: string,
  bodyMarkdown: string,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ["helpdesk", "preview", articleId, lang, bodyMarkdown] as const,
    queryFn: async (): Promise<HelpValidation> => {
      const response = await apiClientV2.post(
        `/help/admin/articles/${articleId}/validate`,
        { title: "preview", bodyMarkdown },
        { params: { lang } },
      )
      return response.data as HelpValidation
    },
    enabled: enabled && bodyMarkdown.trim().length > 0,
    staleTime: 5 * 60 * 1000,
    retry: false,
  })
}

export function useSaveHelpDraft(articleId: string, lang: string) {
  const queryClient = useQueryClient()
  const { user } = useUser()
  const organization = user?.organization

  return useMutation({
    mutationFn: async (body: HelpArticleEdit): Promise<HelpValidation> => {
      const response = await apiClientV2.put(
        `/help/admin/articles/${articleId}/draft`,
        body,
        { params: { lang } },
      )
      return response.data as HelpValidation
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: sourceKey(organization, articleId, lang),
      })
    },
  })
}

export function usePublishHelpArticle(articleId: string, lang: string) {
  const queryClient = useQueryClient()
  const { user } = useUser()
  const organization = user?.organization

  return useMutation({
    mutationFn: async (body: HelpArticleEdit): Promise<HelpValidation> => {
      const response = await apiClientV2.post(
        `/help/admin/articles/${articleId}/publish`,
        body,
        { params: { lang } },
      )
      return response.data as HelpValidation
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: sourceKey(organization, articleId, lang),
      })
      // Every reader's tree now holds the previous text.
      void queryClient.invalidateQueries({
        queryKey: ["helpdesk", "content"],
      })
    },
  })
}

/** The backend's own refusal (stale version, unrenderable body), or null for anything else. */
export function helpEditErrorCode(error: unknown): string | null {
  const detail = (
    error as AxiosError<{ detail?: { error_code?: string } | string }>
  )?.response?.data?.detail
  return typeof detail === "object" && detail !== null
    ? (detail.error_code ?? null)
    : null
}

// --- Structure -------------------------------------------------------------
//
// Every mutation answers with the whole tree, so the cache is set from the
// response rather than invalidated and refetched — one round trip, and no window
// where a create has succeeded but the list beside it still shows the old world.

const treeKey = (organization: string | undefined, lang: string) =>
  ["helpdesk", "tree", organization, lang] as const

/** Every section, group and page — including the ones readers cannot see. */
export function useHelpAdminTree(lang: string, enabled: boolean) {
  const { user } = useUser()
  const organization = user?.organization

  return useQuery({
    queryKey: treeKey(organization, lang),
    queryFn: async (): Promise<HelpAdminTree> => {
      const response = await apiClientV2.get("/help/admin/tree", {
        params: { lang },
      })
      return response.data as HelpAdminTree
    },
    enabled: enabled && Boolean(organization),
    staleTime: 0,
  })
}

/**
 * One hook for every structural change, because they all answer with the tree
 * and all invalidate the reader's content in exactly the same way.
 */
export function useHelpStructureMutation(lang: string) {
  const queryClient = useQueryClient()
  const { user } = useUser()
  const organization = user?.organization

  return useMutation({
    mutationFn: async (
      request: () => Promise<{ data: unknown }>,
    ): Promise<HelpAdminTree> => {
      const response = await request()
      return response.data as HelpAdminTree
    },
    onSuccess: (tree) => {
      queryClient.setQueryData(treeKey(organization, lang), tree)
      // Publishing a section, archiving a page or a reorder all change what
      // readers get back.
      void queryClient.invalidateQueries({ queryKey: ["helpdesk", "content"] })
    },
  })
}

export const helpStructureRequests = {
  createCategory: (body: HelpCategoryCreate) => () =>
    apiClientV2.post("/help/admin/categories", body),
  // Neither patch takes a slug: ids are permanent, so they are not in the patch
  // surface at all rather than being ignored server-side.
  patchCategory:
    (
      id: string,
      body: Partial<Omit<HelpCategoryCreate, "slug">> & {
        isPublished?: boolean
      },
    ) =>
    () =>
      apiClientV2.patch(`/help/admin/categories/${id}`, body),
  createGroup: (body: HelpGroupCreate) => () =>
    apiClientV2.post("/help/admin/groups", body),
  patchGroup:
    (id: string, body: Partial<Pick<HelpGroupCreate, "labels">>) => () =>
      apiClientV2.patch(`/help/admin/groups/${id}`, body),
  patchArticle:
    (
      id: string,
      body: {
        groupId?: string
        requiredFlags?: string[]
        status?: HelpContentStatus
      },
    ) =>
    () =>
      apiClientV2.patch(`/help/admin/articles/${id}/placement`, body),
  reorder:
    (kind: HelpOrderKind, parentId: string | null, ids: string[]) => () =>
      apiClientV2.put("/help/admin/order", { kind, parentId, ids }),
}

/** Creating a page answers with its id, not the tree: the next move is to write it. */
export function useCreateHelpArticle(lang: string) {
  const queryClient = useQueryClient()
  const { user } = useUser()
  const organization = user?.organization

  return useMutation({
    mutationFn: async (body: HelpArticleCreate): Promise<string> => {
      const response = await apiClientV2.post("/help/admin/articles", body)
      return (response.data as { id: string }).id
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: treeKey(organization, lang),
      })
    },
  })
}

/** Every version of an article, newest first — the audit trail behind the editor. */
export function useHelpArticleHistory(
  articleId: string,
  lang: string,
  enabled: boolean,
) {
  const { user } = useUser()
  const organization = user?.organization

  return useQuery({
    queryKey: ["helpdesk", "history", organization, articleId, lang] as const,
    queryFn: async (): Promise<HelpArticleHistory> => {
      const response = await apiClientV2.get(
        `/help/admin/articles/${articleId}/history`,
        { params: { lang } },
      )
      return response.data as HelpArticleHistory
    },
    enabled: enabled && Boolean(organization),
    staleTime: 0,
  })
}
